import React, { useEffect, useRef, useState } from 'react';
import { hero } from '../../content/site';
import { ArrowRight } from '../Common/Icons';

const POSTER = '/media/hero-poster.jpg';
const VIDEO = '/media/hero.mp4';

/**
 * Hero with a background video over a poster image.
 *
 * The poster paints immediately; the video is only attached once the browser
 * is idle, and is skipped entirely on reduced-motion or save-data. If the
 * video fails to decode the poster simply stays put — the previous site
 * shipped HEVC in a .MOV, which many browsers cannot play at all.
 */
const Hero = () => {
  const videoRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection && navigator.connection.saveData;

    if (reducedMotion || saveData) return undefined;

    const start = () => setShowVideo(true);
    const id =
      'requestIdleCallback' in window
        ? window.requestIdleCallback(start, { timeout: 1500 })
        : setTimeout(start, 400);

    return () => {
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  /**
   * Browsers pause background video when the tab is hidden or the window is
   * occluded, and some do not resume on their own. Nudge it back when the page
   * becomes visible again. play() rejects when autoplay is blocked — that is
   * fine, the poster stays.
   */
  useEffect(() => {
    if (!showVideo) return undefined;

    const resume = () => {
      const video = videoRef.current;
      if (!video || document.hidden || !video.paused) return;
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    };

    resume();
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    return () => {
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
  }, [showVideo]);

  return (
    <section className="hero" aria-label="Introduction">
      <div className="hero__media">
        <div className="hero__poster">
          <img
            src={POSTER}
            alt=""
            width="1920"
            height="960"
            fetchpriority="high"
            decoding="async"
          />
        </div>

        {showVideo && (
          <video
            ref={videoRef}
            src={VIDEO}
            poster={POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            tabIndex={-1}
            onCanPlay={(e) => {
              const attempt = e.currentTarget.play();
              if (attempt && attempt.catch) attempt.catch(() => {});
            }}
          />
        )}
      </div>

      <div className="hero__scrim" />

      <div className="hero__inner">
        <p className="eyebrow hero__eyebrow">{hero.eyebrow}</p>
        <h1 className="hero__title">{hero.title}</h1>
        <p className="hero__lead">{hero.lead}</p>

        <div className="hero__actions">
          <a href={hero.primaryCta.href} className="btn">
            {hero.primaryCta.label}
            <ArrowRight />
          </a>
          <a href={hero.secondaryCta.href} className="btn btn--ghost">
            {hero.secondaryCta.label}
          </a>
        </div>
      </div>

      <span className="hero__scroll" aria-hidden="true">
        Scroll
      </span>
    </section>
  );
};

export default Hero;
